import React, { useState, useEffect } from 'react';
import { useShop } from '../../context/ShopContext';
import { defaultHeroBanner } from '../../data/products';
import { 
  Sparkles, 
  Save, 
  RotateCcw, 
  Eye, 
  Image as ImageIcon, 
  ArrowRight, 
  Plus, 
  Trash2, 
  Copy, 
  MoveLeft, 
  MoveRight, 
  Video, 
  CheckCircle2, 
  Layers, 
  ChevronLeft, 
  ChevronRight,
  ExternalLink,
  Upload,
  Tag,
  MessageCircle,
  Phone,
  Megaphone
} from 'lucide-react';

export default function AdminHeroManager() {
  const { 
    heroBannerData, 
    updateHeroBanner, 
    marketingOfferData, 
    updateMarketingOfferData, 
    categories, 
    addToast 
  } = useShop();

  const [bannerSection, setBannerSection] = useState('hero'); // 'hero' or 'promo'

  // Promotional Offer Strip state
  const [promoForm, setPromoForm] = useState(() => ({
    title: marketingOfferData?.title || "✨ TODAY'S SPECIAL JEWELLERY OFFER ✨",
    subtitle: marketingOfferData?.subtitle || "Gold Jewellery Starting at Special Making Charges",
    discountText: marketingOfferData?.discountText || "Get Up To 50% OFF on Making Charges",
    badge: marketingOfferData?.badge || "Limited Time Offer",
    shopUrl: marketingOfferData?.shopUrl || "/category/all",
    whatsappNumber: marketingOfferData?.whatsappNumber || "+919810023456",
    phoneNumber: marketingOfferData?.phoneNumber || "+919810023456",
    isEnabled: marketingOfferData?.isEnabled !== false
  }));

  useEffect(() => {
    if (marketingOfferData) {
      setPromoForm((prev) => ({ ...prev, ...marketingOfferData }));
    }
  }, [marketingOfferData]);

  const handleSavePromo = (e) => {
    e?.preventDefault();
    updateMarketingOfferData(promoForm);
    addToast('Special Jewellery Promotional Banner updated live!', 'success');
  };

  // Normalize initial form state with slides
  const [form, setForm] = useState(() => {
    const raw = heroBannerData || defaultHeroBanner;
    const initialSlides = (Array.isArray(raw.slides) && raw.slides.length > 0)
      ? raw.slides
      : (defaultHeroBanner.slides || []);
    return {
      ...defaultHeroBanner,
      ...raw,
      slides: initialSlides
    };
  });

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Sync if context updates
  useEffect(() => {
    if (heroBannerData?.slides && heroBannerData.slides.length > 0) {
      setForm((prev) => ({
        ...prev,
        ...heroBannerData,
        slides: heroBannerData.slides
      }));
    }
  }, [heroBannerData]);

  // Ensure active index is within bounds
  const currentSlide = form.slides[activeSlideIndex] || form.slides[0] || {};
  const previewSlide = form.slides[previewSlideIndex] || form.slides[0] || {};

  // Update specific field of active slide
  const handleUpdateActiveSlide = (field, value) => {
    setForm((prev) => {
      const updatedSlides = [...prev.slides];
      updatedSlides[activeSlideIndex] = {
        ...updatedSlides[activeSlideIndex],
        [field]: value
      };
      return {
        ...prev,
        slides: updatedSlides
      };
    });
  };

  // Add new slide
  const handleAddSlide = () => {
    const newSlideId = `slide-${Date.now()}`;
    const newSlide = {
      id: newSlideId,
      badge: '✨ NEW FESTIVE ARRIVALS • 100% HALLMARKED',
      titleLine1: 'Exclusive Handcrafted',
      titleHighlight: 'Royal Jewellery Creations',
      subtitle: 'Discover breathtaking mastercrafts with 100% BIS Hallmarked gold, certified diamonds and nationwide armored transit.',
      primaryBtnText: 'Explore Collection',
      primaryBtnCategory: 'all',
      secondaryBtnText: 'Live Gold Rate Calculator',
      image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1920&q=85',
      videoUrl: ''
    };

    setForm((prev) => ({
      ...prev,
      slides: [...prev.slides, newSlide]
    }));
    setActiveSlideIndex(form.slides.length);
    setPreviewSlideIndex(form.slides.length);
    addToast('New slide added! Customize details and click Save.', 'success');
  };

  // Duplicate current slide
  const handleDuplicateSlide = () => {
    const cloned = {
      ...currentSlide,
      id: `slide-${Date.now()}`,
      titleHighlight: `${currentSlide.titleHighlight || ''} (Copy)`
    };
    const newSlides = [...form.slides];
    newSlides.splice(activeSlideIndex + 1, 0, cloned);
    setForm((prev) => ({ ...prev, slides: newSlides }));
    setActiveSlideIndex(activeSlideIndex + 1);
    setPreviewSlideIndex(activeSlideIndex + 1);
    addToast('Slide duplicated.', 'info');
  };

  // Delete current slide
  const handleDeleteSlide = (indexToDelete) => {
    if (form.slides.length <= 1) {
      addToast('At least one hero slide must remain active.', 'warning');
      return;
    }
    const newSlides = form.slides.filter((_, idx) => idx !== indexToDelete);
    setForm((prev) => ({ ...prev, slides: newSlides }));
    const nextIdx = Math.max(0, indexToDelete - 1);
    setActiveSlideIndex(nextIdx);
    setPreviewSlideIndex(nextIdx);
    addToast('Slide removed.', 'info');
  };

  // Reorder slides
  const handleMoveSlide = (direction) => {
    const targetIdx = direction === 'left' ? activeSlideIndex - 1 : activeSlideIndex + 1;
    if (targetIdx < 0 || targetIdx >= form.slides.length) return;

    const newSlides = [...form.slides];
    const temp = newSlides[activeSlideIndex];
    newSlides[activeSlideIndex] = newSlides[targetIdx];
    newSlides[targetIdx] = temp;

    setForm((prev) => ({ ...prev, slides: newSlides }));
    setActiveSlideIndex(targetIdx);
    setPreviewSlideIndex(targetIdx);
  };

  // Handle image upload from computer (resizes to base64)
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      addToast('Image size should be under 8MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1920;
        let width = img.width;
        let height = img.height;

        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.88);
        handleUpdateActiveSlide('image', compressedDataUrl);
        handleUpdateActiveSlide('videoUrl', '');
        handleUpdateActiveSlide('video', '');
        addToast('Banner photo uploaded! Click Save to publish changes live.', 'success');
      };
      img.src = event.target?.result;
    };
    reader.readAsDataURL(file);
  };

  // Preset templates
  const handleApplyPreset = (presetType) => {
    if (presetType === 'solitaires') {
      handleUpdateActiveSlide('badge', '✨ CERTIFIED SOLITAIRES & POLKI • 0% MIDDLEMEN MARKUP');
      handleUpdateActiveSlide('titleLine1', 'Eternal Sparkle of Certified Solitaires &');
      handleUpdateActiveSlide('titleHighlight', 'Artisan Heirloom Gold');
      handleUpdateActiveSlide('subtitle', 'Direct workshop pricing with complete transparency, laser-inscribed HUID hallmark purity, and insured armored nationwide transit.');
      handleUpdateActiveSlide('primaryBtnText', 'VIEW NEW ARRIVALS');
      handleUpdateActiveSlide('videoUrl', 'https://assets.mixkit.co/videos/34427/34427-720.mp4');
      handleUpdateActiveSlide('image', 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1920&q=85');
    } else if (presetType === 'rajputana') {
      handleUpdateActiveSlide('badge', '👑 ROYAL RAJPUTANA HERITAGE • 100% BIS 916 PURE GOLD');
      handleUpdateActiveSlide('titleLine1', 'The Sovereign Splendour of');
      handleUpdateActiveSlide('titleHighlight', 'Royal Rajputana Jewellery');
      handleUpdateActiveSlide('subtitle', 'Experience generational royal craftsmanship with 100% BIS hallmarked 22KT gold, uncut polki chokers, and certified solitaires.');
      handleUpdateActiveSlide('primaryBtnText', 'Explore Royal Collections');
      handleUpdateActiveSlide('videoUrl', 'https://assets.mixkit.co/videos/20877/20877-720.mp4');
      handleUpdateActiveSlide('image', 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1920&q=85');
    } else if (presetType === 'silverware') {
      handleUpdateActiveSlide('badge', '🥈 PURE 999 & 800 HERITAGE SILVERWARE & UTENSILS');
      handleUpdateActiveSlide('titleLine1', 'Sacred Pooja Silver & Artisanal');
      handleUpdateActiveSlide('titleHighlight', 'Chandi Bartan Collection');
      handleUpdateActiveSlide('subtitle', 'Handcrafted pure silver dining thali sets, pooja kalash, silver coins and customized luxury corporate giftware.');
      handleUpdateActiveSlide('primaryBtnText', 'Shop Silverware');
      handleUpdateActiveSlide('videoUrl', '');
      handleUpdateActiveSlide('image', 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=1920&q=85');
    }
    addToast(`Template applied to Slide ${activeSlideIndex + 1}`, 'info');
  };

  // Reset to default slides
  const handleReset = () => {
    if (window.confirm('Reset all hero banner slides to standard factory showroom defaults?')) {
      setForm(defaultHeroBanner);
      updateHeroBanner(defaultHeroBanner);
      setActiveSlideIndex(0);
      setPreviewSlideIndex(0);
      addToast('Hero slider reset to royal defaults!', 'info');
    }
  };

  // Submit and save
  const handleSave = (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Also sync top-level fields from slide 0 for backward compatibility
      const firstSlide = form.slides[0] || {};
      const payload = {
        ...form,
        badge: firstSlide.badge || form.badge,
        titleLine1: firstSlide.titleLine1 || form.titleLine1,
        titleHighlight: firstSlide.titleHighlight || form.titleHighlight,
        subtitle: firstSlide.subtitle || form.subtitle,
        primaryBtnText: firstSlide.primaryBtnText || form.primaryBtnText,
        secondaryBtnText: firstSlide.secondaryBtnText || form.secondaryBtnText,
        heroImage: firstSlide.image || form.heroImage
      };

      updateHeroBanner(payload);
      addToast(`Published ${form.slides.length} Hero Slides live to storefront!`, 'success');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
      
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.2rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
            <Sparkles size={13} /> Visual Storefront Banners
          </div>
          <h2 style={{ fontSize: '1.55rem', fontWeight: 800, color: '#fff', margin: 0 }}>
            Banners & <span className="text-gold-gradient">Home Sliders Manager</span>
          </h2>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0.3rem 0 0' }}>
            Upload photos, add new banners, customize links and promotional offer strips live on your website.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {bannerSection === 'hero' ? (
            <>
              <button
                type="button"
                onClick={handleAddSlide}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.55rem 1.1rem',
                  borderRadius: '4px',
                  background: '#2874f0',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(40,116,240,0.4)'
                }}
              >
                <Plus size={16} /> ➕ Add New Banner (नया बैनर जोड़ें)
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="btn btn-glass btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem' }}
              >
                <RotateCcw size={14} />
                <span>Reset Defaults</span>
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-gold btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.82rem', fontWeight: 800 }}
              >
                <Save size={15} />
                <span>{isSaving ? 'Publishing...' : 'Save & Publish Banners'}</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleSavePromo}
              className="btn btn-gold btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.82rem', fontWeight: 800 }}
            >
              <Save size={15} />
              <span>Save & Publish Offer Banner</span>
            </button>
          )}
        </div>
      </div>

      {/* Banner Category Switcher Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.8rem',
        marginBottom: '1.5rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        paddingBottom: '0.8rem',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={() => setBannerSection('hero')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.65rem 1.2rem',
            borderRadius: '4px',
            border: 'none',
            fontSize: '0.85rem',
            fontWeight: 700,
            cursor: 'pointer',
            backgroundColor: bannerSection === 'hero' ? '#2874f0' : 'rgba(255, 255, 255, 0.08)',
            color: '#ffffff',
            boxShadow: bannerSection === 'hero' ? '0 2px 6px rgba(40, 116, 240, 0.4)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          <ImageIcon size={16} />
          <span>🖼️ Main Hero Slider Banners ({form.slides.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setBannerSection('promo')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.65rem 1.2rem',
            borderRadius: '4px',
            border: 'none',
            fontSize: '0.85rem',
            fontWeight: 700,
            cursor: 'pointer',
            backgroundColor: bannerSection === 'promo' ? '#2874f0' : 'rgba(255, 255, 255, 0.08)',
            color: '#ffffff',
            boxShadow: bannerSection === 'promo' ? '0 2px 6px rgba(40, 116, 240, 0.4)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          <Tag size={16} />
          <span>🏷️ Special Promotional Strip Banner ({promoForm.isEnabled ? '✅ Active' : 'Hidden'})</span>
        </button>
      </div>

      {bannerSection === 'promo' ? (
        <form onSubmit={handleSavePromo} style={{
          background: '#13131f',
          border: '1px solid var(--border-gold)',
          borderRadius: '6px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '0.8rem', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.8rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#fff' }}>
                Special Promotional <span className="text-gold-gradient">Jewellery Offer Strip Banner</span>
              </h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                This high-converting promotional ribbon displays on the Homepage, Live Gold Rates, and Price Calculator.
              </p>
            </div>
            
            {/* Active Toggle Switch */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', background: 'rgba(255,255,255,0.06)', padding: '6px 14px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <input 
                type="checkbox" 
                checked={promoForm.isEnabled}
                onChange={(e) => setPromoForm({ ...promoForm, isEnabled: e.target.checked })}
                style={{ width: '16px', height: '16px', accentColor: '#2874f0', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: promoForm.isEnabled ? '#22c55e' : '#94a3b8' }}>
                {promoForm.isEnabled ? '✅ Banner is LIVE on Storefront' : '❌ Banner Hidden'}
              </span>
            </label>
          </div>

          {/* Live Real-time Storefront Preview */}
          <div style={{ marginBottom: '1.6rem' }}>
            <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', fontWeight: 700, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              👁️ Real-time Storefront Live Preview:
            </span>
            <div style={{
              background: 'linear-gradient(135deg, #781313 0%, #a81c1c 45%, #821414 100%)',
              borderRadius: '8px',
              border: '2px solid #e0b046',
              padding: '1.2rem 1.6rem',
              color: '#ffffff',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              boxShadow: '0 8px 24px rgba(138, 26, 26, 0.3)'
            }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(255, 229, 0, 0.2)', border: '1px solid rgba(255, 229, 0, 0.4)', color: '#ffe500', fontSize: '0.72rem', fontWeight: 800, padding: '2px 8px', borderRadius: '3px', marginBottom: '6px' }}>
                  <Sparkles size={11} /> {promoForm.badge}
                </div>
                <h4 style={{ margin: '0 0 4px', fontSize: '1.15rem', fontWeight: 800, color: '#ffffff' }}>
                  {promoForm.title}
                </h4>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#ffe500', marginBottom: '4px' }}>
                  {promoForm.discountText}
                </div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)' }}>
                  {promoForm.subtitle}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button type="button" style={{ background: '#ffe500', color: '#781313', border: 'none', padding: '9px 18px', borderRadius: '4px', fontWeight: 800, fontSize: '0.86rem' }}>
                  Explore Special Offer →
                </button>
                <button type="button" style={{ background: '#25D366', color: '#ffffff', border: 'none', padding: '9px 15px', borderRadius: '4px', fontWeight: 700, fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MessageCircle size={15} /> WhatsApp Enquiry
                </button>
              </div>
            </div>
          </div>

          {/* Form Fields Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.2rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                Top Deal Badge (पीला टैग):
              </label>
              <input
                type="text"
                className="input-luxury"
                value={promoForm.badge}
                onChange={(e) => setPromoForm({ ...promoForm, badge: e.target.value })}
                placeholder="e.g. Limited Time Festive Offer"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                Main Banner Title (मुख्य हैडिंग):
              </label>
              <input
                type="text"
                className="input-luxury"
                value={promoForm.title}
                onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                placeholder="e.g. ✨ TODAY'S SPECIAL JEWELLERY OFFER ✨"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                Discount Highlight (ऑफर डिस्काउंट टेक्स्ट):
              </label>
              <input
                type="text"
                className="input-luxury"
                value={promoForm.discountText}
                onChange={(e) => setPromoForm({ ...promoForm, discountText: e.target.value })}
                placeholder="e.g. Get Up To 50% OFF on Making Charges"
                style={{ color: '#ffe500', fontWeight: 800 }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                Subtitle (छोटा विवरण):
              </label>
              <input
                type="text"
                className="input-luxury"
                value={promoForm.subtitle}
                onChange={(e) => setPromoForm({ ...promoForm, subtitle: e.target.value })}
                placeholder="e.g. Gold Jewellery Starting at Special Making Charges"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                Redirect Link (बटन क्लिक पर जाने वाला पेज):
              </label>
              <select
                className="input-luxury"
                value={promoForm.shopUrl}
                onChange={(e) => setPromoForm({ ...promoForm, shopUrl: e.target.value })}
                style={{ cursor: 'pointer' }}
              >
                <option value="/category/all">All Royal Jewellery (/category/all)</option>
                <option value="/category/necklaces">Royal Necklaces & Chokers</option>
                <option value="/category/bangles">Artisan Bangles & Kadas</option>
                <option value="/category/rings">Solitaire Rings & Bands</option>
                <option value="/category/silverware">Heritage Silverware & Utensils</option>
                <option value="/live-rates">Live Bullion Rates Page</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
                WhatsApp Number for Enquiries:
              </label>
              <input
                type="text"
                className="input-luxury"
                value={promoForm.whatsappNumber}
                onChange={(e) => setPromoForm({ ...promoForm, whatsappNumber: e.target.value })}
                placeholder="e.g. +919810023456"
              />
            </div>
          </div>

          {/* Bottom Save Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1.2rem', flexWrap: 'wrap', gap: '0.8rem' }}>
            <span style={{ fontSize: '0.82rem', color: '#22c55e', fontWeight: 600 }}>
              <CheckCircle2 size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} />
              Live instant update on storefront across Homepage and Live Rates
            </span>
            <button
              type="submit"
              className="btn btn-gold"
              style={{ padding: '0.65rem 1.8rem', fontSize: '0.88rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Save size={16} />
              <span>Save & Publish Offer Banner</span>
            </button>
          </div>
        </form>
      ) : (
        <>
      {/* ========================================================================= */}
      {/* 1. REAL-TIME STOREFRONT LIVE PREVIEW (MATCHES EXACT USER PHOTO LAYOUT) */}
      {/* ========================================================================= */}
      <div style={{
        background: '#0d0d16',
        border: '1.5px solid var(--border-gold)',
        borderRadius: '6px',
        padding: '1.2rem',
        marginBottom: '1.8rem',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.6)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gold-400)', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>
            <Eye size={16} /> Real-Time Live Preview (What Patrons See on Home Page)
          </div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            Viewing Preview Slide <strong>{previewSlideIndex + 1} of {form.slides.length}</strong>
          </div>
        </div>

        {/* Outer Banner Card matching Flipkart/Luxury Storefront */}
        <div style={{
          position: 'relative',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: '#0a0a10',
          minHeight: '260px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {/* Background Video or Image */}
          {(previewSlide.videoUrl || previewSlide.video) ? (
            <video
              key={`preview-video-${previewSlideIndex}`}
              autoPlay
              loop
              muted
              playsInline
              poster={previewSlide.image}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.55
              }}
            >
              <source src={previewSlide.videoUrl || previewSlide.video} type="video/mp4" />
            </video>
          ) : (
            <img
              src={previewSlide.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1920&q=85'}
              alt="Slide background"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.5
              }}
            />
          )}

          {/* Dark Royal Gradient Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, rgba(10, 10, 15, 0.95) 0%, rgba(10, 10, 15, 0.8) 50%, rgba(10, 10, 15, 0.35) 100%)',
            zIndex: 2
          }} />

          {/* Slide Text Content (Matches user photo) */}
          <div style={{
            position: 'relative',
            zIndex: 4,
            padding: '1.8rem 2.2rem',
            maxWidth: '680px'
          }}>
            {/* Badges Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.22rem 0.65rem',
                background: '#ffe500',
                borderRadius: '2px',
                color: '#2874f0',
                fontSize: '0.74rem',
                fontWeight: 800,
                letterSpacing: '0.03em',
                textTransform: 'uppercase'
              }}>
                <Sparkles size={12} color="#2874f0" />
                {previewSlide.badge || '✨ CERTIFIED SOLITAIRES & POLKI • 0% MIDDLEMEN MARKUP'}
              </span>

              {(previewSlide.video || previewSlide.videoUrl) && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'rgba(0,0,0,0.7)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#ffffff',
                  fontSize: '0.64rem',
                  fontWeight: 700,
                  padding: '0.2rem 0.5rem',
                  borderRadius: '2px'
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00e676', display: 'inline-block' }} />
                  HD CINEMATIC
                </span>
              )}
            </div>

            {/* Headline */}
            <h3 style={{
              fontSize: '1.45rem',
              fontWeight: 800,
              color: '#ffffff',
              margin: '0 0 0.45rem',
              lineHeight: 1.25,
              textShadow: '0 2px 8px rgba(0,0,0,0.8)'
            }}>
              {previewSlide.titleLine1 || 'Eternal Sparkle of Certified Solitaires &'}{' '}
              <span style={{ color: '#ffe500', display: 'inline-block' }}>
                {previewSlide.titleHighlight || 'Artisan Heirloom Gold'}
              </span>
            </h3>

            {/* Subtitle */}
            <p style={{
              color: 'rgba(255, 255, 255, 0.92)',
              fontSize: '0.82rem',
              lineHeight: 1.45,
              margin: '0 0 1rem',
              maxWidth: '560px'
            }}>
              {previewSlide.subtitle || 'Direct workshop pricing with complete transparency, laser-inscribed HUID hallmark purity, and insured armored nationwide transit.'}
            </p>

            {/* Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button style={{
                background: '#ff9f00',
                color: '#ffffff',
                border: 'none',
                borderRadius: '2px',
                fontSize: '0.8rem',
                padding: '0.55rem 1.25rem',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}>
                <span>{previewSlide.primaryBtnText || 'VIEW NEW ARRIVALS'}</span>
                <ArrowRight size={14} />
              </button>

              <button style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                color: '#ffffff',
                border: '1.5px solid #ffffff',
                borderRadius: '2px',
                fontSize: '0.8rem',
                padding: '0.5rem 1.15rem',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}>
                <span>{previewSlide.secondaryBtnText || 'LIVE GOLD RATE CALCULATOR'}</span>
              </button>
            </div>
          </div>

          {/* Left Arrow */}
          <button
            onClick={() => setPreviewSlideIndex((prev) => (prev - 1 + form.slides.length) % form.slides.length)}
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '32px',
              height: '42px',
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              borderRadius: '2px',
              color: '#ffffff',
              cursor: 'pointer',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronLeft size={20} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => setPreviewSlideIndex((prev) => (prev + 1) % form.slides.length)}
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '32px',
              height: '42px',
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              borderRadius: '2px',
              color: '#ffffff',
              cursor: 'pointer',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots Indicator */}
          <div style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '6px',
            zIndex: 5
          }}>
            {form.slides.map((_, dotIdx) => (
              <span
                key={dotIdx}
                onClick={() => setPreviewSlideIndex(dotIdx)}
                style={{
                  width: previewSlideIndex === dotIdx ? '22px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: previewSlideIndex === dotIdx ? '#2874f0' : 'rgba(255, 255, 255, 0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. SLIDE SELECTOR & MANAGEMENT TABS */}
      {/* ========================================================================= */}
      <div style={{
        background: '#13131f',
        border: '1px solid var(--border-gold)',
        borderRadius: '6px',
        padding: '1.2rem',
        marginBottom: '1.8rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.8rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Layers size={17} color="var(--gold-400)" />
              <span>Select Slide to Edit ({form.slides.length} Slides Configured)</span>
            </h3>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
              Click any slide below to customize its text, video background, buttons and imagery.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleAddSlide}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: '#2874f0',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '0.55rem 1.1rem',
                fontSize: '0.82rem',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(40,116,240,0.4)'
              }}
            >
              <Plus size={16} /> ➕ Add New Banner (नया बैनर जोड़ें)
            </button>

            <button
              type="button"
              onClick={handleDuplicateSlide}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                padding: '0.45rem 0.85rem',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <Copy size={14} /> Duplicate Slide
            </button>
          </div>
        </div>

        {/* Slide Tabs */}
        <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '8px' }}>
          {form.slides.map((s, idx) => {
            const isActive = activeSlideIndex === idx;
            return (
              <div
                key={s.id || idx}
                onClick={() => {
                  setActiveSlideIndex(idx);
                  setPreviewSlideIndex(idx);
                }}
                style={{
                  flex: '0 0 auto',
                  minWidth: '220px',
                  maxWidth: '260px',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: isActive ? '2px solid var(--gold-400)' : '1px solid rgba(255,255,255,0.12)',
                  background: isActive ? 'rgba(212, 175, 55, 0.14)' : 'rgba(255, 255, 255, 0.03)',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                {/* Miniature Thumbnail */}
                <div style={{
                  height: '75px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '8px',
                  backgroundColor: '#0a0a10',
                  position: 'relative',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  {s.image ? (
                    <img src={s.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                      <ImageIcon size={22} />
                    </div>
                  )}
                  <span style={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    background: 'rgba(0,0,0,0.75)',
                    color: '#ffffff',
                    fontSize: '0.64rem',
                    fontWeight: 800,
                    padding: '1px 6px',
                    borderRadius: '2px'
                  }}>
                    Banner #{idx + 1}
                  </span>
                  {(s.video || s.videoUrl) && (
                    <span style={{ position: 'absolute', top: 4, right: 4, fontSize: '0.62rem', background: '#059669', color: '#fff', padding: '1px 5px', borderRadius: '3px', fontWeight: 700 }}>
                      VIDEO
                    </span>
                  )}
                </div>

                <div style={{
                  fontSize: '0.84rem',
                  fontWeight: 700,
                  color: isActive ? '#ffffff' : '#cbd5e1',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '2px'
                }}>
                  {s.titleHighlight || s.titleLine1 || `Banner #${idx + 1}`}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.badge || 'No tag'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3. ACTIVE SLIDE EDIT FORM */}
      {/* ========================================================================= */}
      <form onSubmit={handleSave} style={{
        background: '#13131f',
        border: '1px solid var(--border-gold)',
        borderRadius: '6px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Title Bar of Editor */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '0.8rem', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.8rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>
              Editing Slide #{activeSlideIndex + 1}: <span className="text-gold-gradient">{currentSlide.titleHighlight || 'Untitled Slide'}</span>
            </h3>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              Unique ID: <code>{currentSlide.id}</code>
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            {/* Move Left / Right */}
            <button
              type="button"
              disabled={activeSlideIndex === 0}
              onClick={() => handleMoveSlide('left')}
              title="Move Slide Earlier"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: activeSlideIndex === 0 ? '#475569' : '#fff',
                border: '1px solid rgba(255,255,255,0.15)',
                padding: '5px 9px',
                borderRadius: '4px',
                cursor: activeSlideIndex === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <MoveLeft size={14} />
            </button>

            <button
              type="button"
              disabled={activeSlideIndex === form.slides.length - 1}
              onClick={() => handleMoveSlide('right')}
              title="Move Slide Later"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: activeSlideIndex === form.slides.length - 1 ? '#475569' : '#fff',
                border: '1px solid rgba(255,255,255,0.15)',
                padding: '5px 9px',
                borderRadius: '4px',
                cursor: activeSlideIndex === form.slides.length - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              <MoveRight size={14} />
            </button>

            {form.slides.length > 1 && (
              <button
                type="button"
                onClick={() => handleDeleteSlide(activeSlideIndex)}
                title="Delete this slide"
                style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.35)',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.76rem',
                  fontWeight: 700
                }}
              >
                <Trash2 size={13} /> Delete Slide
              </button>
            )}
          </div>
        </div>

        {/* Quick Template Presets for this slide */}
        <div style={{
          backgroundColor: 'rgba(212, 175, 55, 0.08)',
          border: '1px solid rgba(212, 175, 55, 0.25)',
          borderRadius: '4px',
          padding: '0.65rem 1rem',
          marginBottom: '1.4rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--gold-400)' }}>
            ⚡ 1-Click Content Presets:
          </span>
          <button
            type="button"
            onClick={() => handleApplyPreset('solitaires')}
            style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: '3px', background: '#1e293b', border: '1px solid #334155', color: '#f8fafc', cursor: 'pointer' }}
          >
            ✨ Solitaires & Polki (As in your photo)
          </button>
          <button
            type="button"
            onClick={() => handleApplyPreset('rajputana')}
            style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: '3px', background: '#1e293b', border: '1px solid #334155', color: '#f8fafc', cursor: 'pointer' }}
          >
            👑 Royal Rajputana 22K Gold
          </button>
          <button
            type="button"
            onClick={() => handleApplyPreset('silverware')}
            style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: '3px', background: '#1e293b', border: '1px solid #334155', color: '#f8fafc', cursor: 'pointer' }}
          >
            🥈 Pure Silverware & Utensils
          </button>
        </div>

        {/* ========================================================= */}
        {/* 📷 PRIMARY BANNER PHOTO (PROMINENT FOR CLIENT) */}
        {/* ========================================================= */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1.5px dashed rgba(212, 175, 55, 0.45)',
          borderRadius: '8px',
          padding: '1.4rem',
          marginBottom: '1.6rem',
          backgroundColor: '#0d0d16'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.6rem' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', fontWeight: 800, color: '#ffe500' }}>
                <Upload size={18} /> 📷 Banner Photo / Image (बैनर फोटो चुनें या अपलोड करें) *
              </label>
              <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                Landscape banner (1920 × 800 px recommended). Mobile & laptop uploads auto-compressed.
              </span>
            </div>

            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.65rem 1.4rem',
              background: '#2874f0',
              color: '#ffffff',
              borderRadius: '4px',
              fontWeight: 800,
              fontSize: '0.84rem',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(40,116,240,0.35)'
            }}>
              <Upload size={16} /> 📁 Upload Photo (कंप्यूटर / मोबाइल से फोटो अपलोड करें)
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {/* Active Photo Preview */}
          {currentSlide.image ? (
            <div style={{
              position: 'relative',
              borderRadius: '6px',
              overflow: 'hidden',
              maxHeight: '220px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backgroundColor: '#000000',
              marginBottom: '1rem'
            }}>
              <img
                src={currentSlide.image}
                alt="Banner Active Photo"
                style={{ width: '100%', maxHeight: '220px', objectFit: 'cover' }}
              />
              <div style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0,0,0,0.8)',
                color: '#22c55e',
                fontSize: '0.74rem',
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <CheckCircle2 size={14} /> Active Banner Photo
              </div>
            </div>
          ) : (
            <div style={{
              padding: '2rem 1rem',
              textAlign: 'center',
              color: '#94a3b8',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '6px',
              marginBottom: '1rem'
            }}>
              <ImageIcon size={32} style={{ color: '#64748b', marginBottom: '8px' }} />
              <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>No banner photo selected yet</div>
              <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Click "Upload Photo" above or paste an Image URL below.</div>
            </div>
          )}

          {/* Or enter Image URL */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ fontSize: '0.76rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>Or Image URL:</span>
            <input
              type="text"
              className="input-luxury"
              value={currentSlide.image || ''}
              onChange={(e) => handleUpdateActiveSlide('image', e.target.value)}
              placeholder="https://images.unsplash.com/photo-..."
              style={{ flex: 1, fontSize: '0.82rem', padding: '7px 10px' }}
            />
          </div>
        </div>

        {/* Form Inputs Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.2rem', marginBottom: '1.5rem' }}>
          
          {/* Top Deal Badge */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
              Top Tag / Deal Badge (Yellow Highlight):
            </label>
            <input
              type="text"
              className="input-luxury"
              value={currentSlide.badge || ''}
              onChange={(e) => handleUpdateActiveSlide('badge', e.target.value)}
              placeholder="e.g. ✨ CERTIFIED SOLITAIRES & POLKI • 0% MIDDLEMEN MARKUP"
              style={{ fontWeight: 700 }}
            />
            <span style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
              Appears in the yellow deal pill at the top of the slide banner.
            </span>
          </div>

          {/* Headline Line 1 */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
              Main Title / Headline (White text):
            </label>
            <input
              type="text"
              className="input-luxury"
              value={currentSlide.titleLine1 || ''}
              onChange={(e) => handleUpdateActiveSlide('titleLine1', e.target.value)}
              placeholder="e.g. Eternal Sparkle of Certified Solitaires &"
            />
          </div>

          {/* Title Highlight (Yellow text) */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
              Title Highlight (Yellow Emphasis Text):
            </label>
            <input
              type="text"
              className="input-luxury"
              value={currentSlide.titleHighlight || ''}
              onChange={(e) => handleUpdateActiveSlide('titleHighlight', e.target.value)}
              placeholder="e.g. Artisan Heirloom Gold"
              style={{ color: '#ffe500', fontWeight: 800 }}
            />
          </div>

          {/* Subtitle / Description */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
              Subtitle / Craftsmanship Description:
            </label>
            <textarea
              className="input-luxury"
              rows={3}
              value={currentSlide.subtitle || ''}
              onChange={(e) => handleUpdateActiveSlide('subtitle', e.target.value)}
              placeholder="e.g. Direct workshop pricing with complete transparency, laser-inscribed HUID hallmark purity, and insured armored nationwide transit."
              style={{ lineHeight: 1.5 }}
            />
          </div>

          {/* Primary Button */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '6px',
            padding: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.8rem', fontSize: '0.85rem', fontWeight: 800, color: '#fb923c' }}>
              🟠 Primary Button (Orange CTA)
            </h4>
            
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={{ display: 'block', fontSize: '0.74rem', color: '#cbd5e1', marginBottom: '0.3rem', fontWeight: 600 }}>
                Button Label:
              </label>
              <input
                type="text"
                className="input-luxury"
                value={currentSlide.primaryBtnText || ''}
                onChange={(e) => handleUpdateActiveSlide('primaryBtnText', e.target.value)}
                placeholder="e.g. VIEW NEW ARRIVALS"
                style={{ fontWeight: 700 }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.74rem', color: '#cbd5e1', marginBottom: '0.3rem', fontWeight: 600 }}>
                Destination Category:
              </label>
              <select
                className="input-luxury"
                value={currentSlide.primaryBtnCategory || 'all'}
                onChange={(e) => handleUpdateActiveSlide('primaryBtnCategory', e.target.value)}
              >
                <option value="all">All Jewels & Collections</option>
                {(categories || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.title || c.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Secondary Button */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '6px',
            padding: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.8rem', fontSize: '0.85rem', fontWeight: 800, color: '#ffffff' }}>
              ⚪ Secondary Button (Outline Action)
            </h4>

            <div>
              <label style={{ display: 'block', fontSize: '0.74rem', color: '#cbd5e1', marginBottom: '0.3rem', fontWeight: 600 }}>
                Button Label:
              </label>
              <input
                type="text"
                className="input-luxury"
                value={currentSlide.secondaryBtnText || ''}
                onChange={(e) => handleUpdateActiveSlide('secondaryBtnText', e.target.value)}
                placeholder="e.g. LIVE GOLD RATE CALCULATOR"
                style={{ fontWeight: 700 }}
              />
            </div>
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.7rem', color: '#64748b' }}>
              Opens the interactive 0% Wastage & Live Bullion Rate Calculator modal.
            </p>
          </div>

          {/* Background Video URL */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--gold-400)', marginBottom: '0.35rem', fontWeight: 700 }}>
              Background Video URL (MP4 / WebM):
            </label>
            <input
              type="text"
              className="input-luxury"
              value={currentSlide.videoUrl || currentSlide.video || ''}
              onChange={(e) => {
                handleUpdateActiveSlide('videoUrl', e.target.value);
                handleUpdateActiveSlide('video', e.target.value);
              }}
              placeholder="e.g. /images/jewelry-model.mp4 or https://assets.mixkit.co/..."
            />
            <span style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
              Tip: Leave empty if you want a pure Image background instead of video.
            </span>
          </div>

          {/* Photo reminder note */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: '6px',
            padding: '0.8rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            color: '#94a3b8',
            fontSize: '0.76rem'
          }}>
            <ImageIcon size={16} color="var(--gold-400)" />
            <span>Banner photo is managed in the prominent <strong>Banner Photo Section</strong> above.</span>
          </div>

        </div>

        {/* Bottom Save Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: '1.2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#22c55e', fontSize: '0.8rem', fontWeight: 600 }}>
            <CheckCircle2 size={16} /> Instant live updates with zero reload needed
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="btn btn-gold"
            style={{
              padding: '0.65rem 1.8rem',
              fontSize: '0.88rem',
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Save size={16} />
            <span>{isSaving ? 'Publishing Changes...' : 'Save & Publish All Slides to Storefront'}</span>
          </button>
        </div>
      </form>
      </>
      )}
    </div>
  );
}
